'use client'

import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { getLenis } from '../hooks/useSmoothScroll'
import { NAV_ITEMS, FIELD_VERBS } from './Navigation'

/**
 * ScrollProgress — vertical SPINE instrument on the fixed right rail.
 *
 * A live docking readout, not a bare progress line: nine hexagon node ticks sit
 * at each section's true document offset. A traveling hex dot rides the rail;
 * as it crosses a node the node fills --color-fire and pulses (a quiet "dock").
 * Nodes ahead of the dot stay --color-steel-deep. A tiny rotated mono readout
 * anchored to the dot reports "NN · LABEL / fieldVerb".
 *
 * DOM-only, aria-hidden, zero WebGL — driven by the existing rAF-throttled
 * scroll handler (Lenis, with a native-scroll fallback for reduced-motion /
 * no-Lenis). Node offsets are cached and only recomputed on resize or when the
 * document height changes (pin-spacers settling), never per frame.
 *
 * Reduced motion: static spine filled to current progress, nodes coloured to
 * state, no dock pulses — matches the calm variant the rest of the site uses.
 */

const HEX_CLIP = 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)'
const NODE_H = 8 // px — used for margin-based vertical centring (no transform)

export default function ScrollProgress() {
  const barRef = useRef<HTMLDivElement>(null)
  const dotRef = useRef<HTMLDivElement>(null)      // wrapper — owns per-frame translate
  const dotInnerRef = useRef<HTMLDivElement>(null) // hex shape — owns GSAP pulse (scale)
  const readoutRef = useRef<HTMLDivElement>(null)  // wrapper — owns per-frame translate
  const readoutTextRef = useRef<HTMLSpanElement>(null)
  const nodeRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let raf = 0
    let scheduled = false
    let usingNative = false

    const n = NAV_ITEMS.length
    let railH = window.innerHeight
    const fractions: number[] = new Array(n).fill(0) // each node's 0..1 rail position
    const passed: boolean[] = new Array(n).fill(false)
    let lastActive = -1
    let lastScrollHeight = 0

    const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)

    // Native scroll fallback (reduced-motion users have no Lenis instance).
    const nativeProgress = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight
      return max > 0 ? Math.min(window.scrollY / max, 1) : 0
    }

    // Cache each section's true document offset as a rail fraction. Mirrors
    // Navigation.computeActive's pin-spacer handling so the ticks line up with
    // where the sections actually START. Cheap enough to run on resize / layout
    // shifts, but never per frame.
    const computeOffsets = () => {
      railH = window.innerHeight
      lastScrollHeight = document.documentElement.scrollHeight
      const max = lastScrollHeight - window.innerHeight
      for (let i = 0; i < n; i++) {
        const el = document.getElementById(NAV_ITEMS[i].target.slice(1))
        let frac = n > 1 ? i / (n - 1) : 0 // sane fallback before layout settles
        if (el) {
          const parent = el.parentElement
          const target = parent?.classList.contains('pin-spacer') ? parent : el
          const rect = target.getBoundingClientRect()
          const docTop = rect.top + window.scrollY
          frac = max > 0 ? clamp01(docTop / max) : 0
        }
        fractions[i] = frac
        const node = nodeRefs.current[i]
        if (node) node.style.top = `${frac * 100}%`
      }
    }

    const apply = (p: number) => {
      // Spine fill (origin top).
      if (barRef.current) barRef.current.style.transform = `scaleY(${p})`

      const y = p * railH

      // Traveling dot (wrapper owns translate only).
      if (dotRef.current) {
        dotRef.current.style.transform = `translateX(50%) translateY(${y}px) translateY(-50%)`
      }

      // Node states + dock pulse on cross. active = last node the dot has reached.
      let active = 0
      for (let i = 0; i < n; i++) {
        const isPassed = p + 0.0002 >= fractions[i]
        if (isPassed) active = i
        if (isPassed !== passed[i]) {
          passed[i] = isPassed
          const node = nodeRefs.current[i]
          if (node) {
            node.style.background = isPassed ? 'var(--color-fire)' : 'var(--color-steel-deep)'
            node.style.boxShadow = isPassed ? '0 0 8px var(--color-fire)' : 'none'
            node.style.opacity = isPassed ? '1' : '0.55'
            // Dock pulse — one-shot tween on the node itself (scale only).
            if (isPassed && !reduce) {
              gsap.fromTo(node, { scale: 1.9 }, { scale: 1, duration: 0.5, ease: 'power3.out' })
            }
          }
        }
      }

      // Readout follows the dot; text only rewrites when the active node changes.
      if (readoutRef.current) readoutRef.current.style.transform = `translateY(${y}px) translateY(-50%)`
      if (active !== lastActive) {
        lastActive = active
        const item = NAV_ITEMS[active]
        const verb = FIELD_VERBS[item.target.slice(1)] ?? ''
        if (readoutTextRef.current) {
          readoutTextRef.current.textContent = `${item.num} · ${item.label}${verb ? ' / ' + verb : ''}`
        }
        // Quiet dock flare on the dot when it reaches a new node.
        if (!reduce && dotInnerRef.current) {
          gsap.fromTo(dotInnerRef.current, { scale: 1.7 }, { scale: 1, duration: 0.45, ease: 'power3.out' })
        }
      }
    }

    const update = () => {
      scheduled = false
      const lenis = getLenis()
      // Recompute offsets when the document grows/shrinks (pin-spacers settling,
      // late images). Comparison is a plain read — no per-frame rect work.
      if (document.documentElement.scrollHeight !== lastScrollHeight) computeOffsets()
      apply(lenis ? lenis.progress : nativeProgress())
    }

    const onScroll = () => {
      if (!scheduled) {
        scheduled = true
        raf = requestAnimationFrame(update)
      }
    }

    const onResize = () => {
      computeOffsets()
      onScroll()
    }

    // Wait for Lenis to initialize, then attach scroll listener. If it never
    // appears (reduced motion), fall back to the native scroll event.
    let attempts = 0
    const checkLenis = setInterval(() => {
      attempts++
      const lenis = getLenis()
      if (lenis) {
        clearInterval(checkLenis)
        computeOffsets()
        lenis.on('scroll', onScroll)
        update() // initial position
      } else if (attempts >= 8) {
        clearInterval(checkLenis)
        usingNative = true
        computeOffsets()
        window.addEventListener('scroll', onScroll, { passive: true })
        update()
      }
    }, 100)

    window.addEventListener('resize', onResize)

    return () => {
      clearInterval(checkLenis)
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onResize)
      const lenis = getLenis()
      if (lenis) lenis.off('scroll', onScroll)
      if (usingNative) window.removeEventListener('scroll', onScroll)
    }
  }, [])

  return (
    <div
      className="fixed top-0 right-0 w-0.5 h-full pointer-events-none"
      style={{ zIndex: 9998 }}
      aria-hidden="true"
    >
      {/* Idle spine track (steel), so the rail reads as an instrument at rest */}
      <div
        className="absolute inset-0 w-full h-full"
        style={{ backgroundColor: 'var(--color-steel-deep)', opacity: 0.18 }}
      />

      {/* Progress fill */}
      <div
        ref={barRef}
        className="absolute inset-0 w-full h-full origin-top"
        style={{
          backgroundColor: 'var(--color-fire)',
          transform: 'scaleY(0)',
          willChange: 'transform',
        }}
      />

      {/* Section node ticks — top set at runtime from true document offsets */}
      {NAV_ITEMS.map((item, i) => (
        <div
          key={item.target}
          ref={(el) => { nodeRefs.current[i] = el }}
          className="absolute"
          style={{
            top: 0,
            right: -2.5,               // centre a 7px node on the 2px rail
            marginTop: -NODE_H / 2,     // vertical centre without using transform
            width: 7,
            height: NODE_H,
            backgroundColor: 'var(--color-steel-deep)',
            opacity: 0.55,
            clipPath: HEX_CLIP,
            willChange: 'transform',
          }}
        />
      ))}

      {/* Rotated mono readout anchored to the moving dot */}
      <div
        ref={readoutRef}
        className="absolute"
        style={{ top: 0, right: 12, willChange: 'transform' }}
      >
        <span
          ref={readoutTextRef}
          className="font-mono uppercase"
          style={{
            display: 'inline-block',
            transform: 'rotate(-90deg)',
            transformOrigin: 'right center',
            whiteSpace: 'nowrap',
            fontSize: '8px',
            letterSpacing: '0.22em',
            color: 'var(--color-mist)',
            opacity: 0.7,
          }}
        />
      </div>

      {/* Traveling hex dot — wrapper translates, inner hex pulses */}
      <div
        ref={dotRef}
        className="absolute right-1/2"
        style={{ top: 0, willChange: 'transform' }}
      >
        <div
          ref={dotInnerRef}
          style={{
            width: '6px',
            height: '7px',
            backgroundColor: 'var(--color-fire)',
            clipPath: HEX_CLIP,
            boxShadow: '0 0 6px rgba(255,107,0,0.6)',
            willChange: 'transform',
          }}
        />
      </div>
    </div>
  )
}
